import { useId, useRef } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { LogoFieldActions, LogoFieldState } from "./logo-field-controller";

/**
 * Picks a logo file, hosts it, and shows the result. The page's controller owns
 * a `LogoFieldController` and reads the URL back from its state on save.
 */
export function LogoField({ label = "Logo", labelClassName = "label" }: { label?: string; labelClassName?: string }) {
  const state = useSubtree(LogoFieldState);
  const actions = useSubtree(LogoFieldActions);
  const observer = useObserver();
  const value = observer.watch(state.url);
  const uploading = observer.watch(state.uploading);
  const error = observer.watch(state.error);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    await actions.pick(file);
    // Clear the input so re-picking the same file after a failure still fires a change event.
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <span className={labelClassName}>
        {label} <span className="font-normal text-faint">(optional)</span>
      </span>

      <div className="flex items-center gap-3">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-btn border border-line bg-surface">
          {value ? (
            <img src={value} alt="Shop logo" className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-faint">No logo</span>
          )}
        </div>

        <div className="min-w-0">
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="sr-only"
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor={inputId}
              className={`btn-chip cursor-pointer ${uploading ? "pointer-events-none opacity-50" : ""}`}
            >
              {uploading ? "Uploading…" : value ? "Replace" : "Upload image"}
            </label>
            {value && !uploading && (
              <button type="button" onClick={actions.clear} className="btn-ghost">
                Remove
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-faint">PNG, JPEG, WEBP, GIF or SVG, up to 2 MB.</p>
        </div>
      </div>

      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
